// Structured CV model. The whole CV lives in a Typst `#let cv = ( … )` data
// block at the top of every default template; the template body renders it
// with `#for` loops. The visual form reads this block into `CvData`, edits it
// (including adding/removing entries), and writes it back — the body render
// never changes, so every section stays editable without touching code.
//
// A small parser/serializer for the Typst value subset we emit (nested
// dicts/arrays of strings) keeps the block the single source of truth, so a
// shared link (#src=) or a localStorage draft round-trips cleanly.

export interface Perfil {
  nombre: string;
  titular: string;
  ubicacion: string;
  telefono: string;
  email: string;
  linkedin: string;
  github: string;
}

export interface ExpItem {
  puesto: string;
  empresa: string;
  fechas: string;
  puntos: string[];
}

export interface EduItem {
  titulo: string;
  centro: string;
  fechas: string;
}

export interface SkillItem {
  etiqueta: string;
  valor: string;
}

export interface CvData {
  perfil: Perfil;
  resumen: string;
  experiencia: ExpItem[];
  formacion: EduItem[];
  certificaciones: string[];
  habilidades: SkillItem[];
}

export const PERFIL_FIELDS: { key: keyof Perfil; label: string; placeholder: string }[] = [
  { key: "nombre", label: "Nombre", placeholder: "Nombre Apellidos" },
  { key: "titular", label: "Titular / rol", placeholder: "Analista de Ciberseguridad" },
  { key: "ubicacion", label: "Ubicación", placeholder: "Ciudad, País" },
  { key: "telefono", label: "Teléfono", placeholder: "+34 600 000 000" },
  { key: "email", label: "Email", placeholder: "correo@dominio.com" },
  { key: "linkedin", label: "LinkedIn (sin https://)", placeholder: "linkedin.com/in/usuario" },
  { key: "github", label: "GitHub (sin https://)", placeholder: "github.com/usuario" },
];

export const DEFAULT_CV: CvData = {
  perfil: {
    nombre: "Nombre Apellidos",
    titular: "Analista de Ciberseguridad · Red Team",
    ubicacion: "Ciudad, País",
    telefono: "+34 600 000 000",
    email: "correo@dominio.com",
    linkedin: "linkedin.com/in/usuario",
    github: "github.com/usuario",
  },
  resumen:
    "Analista de ciberseguridad con X años de experiencia en pentesting y respuesta a incidentes. Especializado en seguridad ofensiva, automatización y hardening de infraestructuras. Incluye aquí 2–3 líneas con las palabras clave de la oferta a la que aplicas.",
  experiencia: [
    {
      puesto: "Pentester Senior",
      empresa: "Empresa S.L.",
      fechas: "2023 — Actualidad",
      puntos: [
        "Lideré auditorías de seguridad para más de 20 clientes (web, red interna, cloud).",
        "Automaticé el reconocimiento con Python reduciendo el tiempo de entrega un 40%.",
        "Redacté informes ejecutivos y técnicos; retest y acompañamiento a remediación.",
      ],
    },
    {
      puesto: "Analista de Seguridad",
      empresa: "Otra Empresa",
      fechas: "2020 — 2023",
      puntos: [
        "Gestioné el SIEM (detección, triage y respuesta) para un parque de 5.000 endpoints.",
        "Reduje falsos positivos un 60% afinando reglas de correlación.",
      ],
    },
  ],
  formacion: [
    { titulo: "Grado en Ingeniería Informática", centro: "Universidad", fechas: "2016 — 2020" },
  ],
  certificaciones: [
    "OSCP — Offensive Security Certified Professional (2024)",
    "eJPT — eLearnSecurity Junior Penetration Tester (2022)",
  ],
  habilidades: [
    { etiqueta: "Ofensiva", valor: "pentesting web (OWASP), Active Directory, redes, móvil" },
    { etiqueta: "Herramientas", valor: "Burp Suite, Nmap, Metasploit, BloodHound, Wireshark" },
    { etiqueta: "Lenguajes", valor: "Python, Bash, PowerShell, SQL" },
    { etiqueta: "Idiomas", valor: "Español (nativo), Inglés (C1)" },
  ],
};

// ── Typst value parser (strings / arrays / dicts only) ────────────────────

type Val = string | Val[] | { [k: string]: Val };

function skipWs(s: string, i: number): number {
  while (i < s.length && /\s/.test(s[i])) i++;
  return i;
}

function parseString(s: string, i: number): [string, number] {
  i++; // opening quote
  let out = "";
  while (i < s.length && s[i] !== '"') {
    if (s[i] === "\\") {
      out += s[i + 1];
      i += 2;
    } else {
      out += s[i];
      i++;
    }
  }
  return [out, i + 1];
}

function parseParen(s: string, i: number): [Val, number] {
  i = skipWs(s, i + 1); // past '('
  if (s[i] === ":") {
    // empty dict `(:)`
    const j = skipWs(s, i + 1);
    return [{}, j + 1];
  }
  if (s[i] === ")") return [[], i + 1]; // empty array `()`
  const arr: Val[] = [];
  const dict: Record<string, Val> = {};
  let isDict: boolean | null = null;
  for (;;) {
    i = skipWs(s, i);
    if (s[i] === ")") {
      i++;
      break;
    }
    const km = /^([A-Za-z_][A-Za-z0-9_]*)\s*:/.exec(s.slice(i));
    if (isDict === null) isDict = !!km;
    if (isDict) {
      if (!km) throw new Error("expected key");
      i += km[0].length;
      const [v, ni] = parseValue(s, i);
      dict[km[1]] = v;
      i = ni;
    } else {
      const [v, ni] = parseValue(s, i);
      arr.push(v);
      i = ni;
    }
    i = skipWs(s, i);
    if (s[i] === ",") {
      i++;
      continue;
    }
    if (s[i] === ")") {
      i++;
      break;
    }
    throw new Error("expected , or )");
  }
  return [isDict ? dict : arr, i];
}

function parseValue(s: string, i: number): [Val, number] {
  i = skipWs(s, i);
  if (s[i] === '"') return parseString(s, i);
  if (s[i] === "(") return parseParen(s, i);
  throw new Error("unexpected token");
}

// Locate `#let cv = ( … )` and return its span + parsed value.
function extractCvBlock(source: string): { start: number; end: number; value: Record<string, Val> } | null {
  const m = /#let\s+cv\s*=\s*/.exec(source);
  if (!m) return null;
  const startParen = m.index + m[0].length;
  if (source[startParen] !== "(") return null;
  try {
    const [val, end] = parseParen(source, startParen);
    if (Array.isArray(val) || typeof val === "string") return null;
    return { start: m.index, end, value: val };
  } catch {
    return null;
  }
}

// ── Typed mapping (defensive: fall back to defaults on shape drift) ───────

const str = (v: Val | undefined, fallback = ""): string => (typeof v === "string" ? v : fallback);
const arr = (v: Val | undefined): Val[] => (Array.isArray(v) ? v : []);
const dict = (v: Val | undefined): Record<string, Val> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, Val>) : {};

function toPerfil(v: Val | undefined): Perfil {
  const d = dict(v);
  return {
    nombre: str(d.nombre),
    titular: str(d.titular),
    ubicacion: str(d.ubicacion),
    telefono: str(d.telefono),
    email: str(d.email),
    linkedin: str(d.linkedin),
    github: str(d.github),
  };
}

/** Read the CvData from a source, or null when there is no valid cv block. */
export function parseCv(source: string): CvData | null {
  const block = extractCvBlock(source);
  if (!block) return null;
  const v = block.value;
  return {
    perfil: toPerfil(v.perfil),
    resumen: str(v.resumen),
    experiencia: arr(v.experiencia).map((e) => {
      const d = dict(e);
      return {
        puesto: str(d.puesto),
        empresa: str(d.empresa),
        fechas: str(d.fechas),
        puntos: arr(d.puntos).map((p) => str(p)),
      };
    }),
    formacion: arr(v.formacion).map((f) => {
      const d = dict(f);
      return { titulo: str(d.titulo), centro: str(d.centro), fechas: str(d.fechas) };
    }),
    certificaciones: arr(v.certificaciones).map((c) => str(c)),
    habilidades: arr(v.habilidades).map((h) => {
      const d = dict(h);
      return { etiqueta: str(d.etiqueta), valor: str(d.valor) };
    }),
  };
}

// ── Serialization (deterministic → round-trip identity) ───────────────────

function esc(v: string): string {
  return v.replace(/([\\"])/g, "\\$1");
}
function q(v: string): string {
  return `"${esc(v)}"`;
}
// Serialize an array of already-rendered item strings at a given indent.
function arrayLines(items: string[], indent: string): string {
  if (items.length === 0) return "()";
  const inner = indent + "  ";
  return "(\n" + items.map((it) => inner + it + ",").join("\n") + "\n" + indent + ")";
}

export function serializeCv(cv: CvData): string {
  const i0 = "";
  const perfil = [
    `nombre: ${q(cv.perfil.nombre)}`,
    `titular: ${q(cv.perfil.titular)}`,
    `ubicacion: ${q(cv.perfil.ubicacion)}`,
    `telefono: ${q(cv.perfil.telefono)}`,
    `email: ${q(cv.perfil.email)}`,
    `linkedin: ${q(cv.perfil.linkedin)}`,
    `github: ${q(cv.perfil.github)}`,
  ];
  const perfilBlock =
    "(\n" + perfil.map((l) => "    " + l + ",").join("\n") + "\n  )";

  const expItems = cv.experiencia.map((e) => {
    const puntos = arrayLines(e.puntos.map(q), "      ");
    return (
      "(\n" +
      `      puesto: ${q(e.puesto)},\n` +
      `      empresa: ${q(e.empresa)},\n` +
      `      fechas: ${q(e.fechas)},\n` +
      `      puntos: ${puntos},\n` +
      "    )"
    );
  });
  const eduItems = cv.formacion.map(
    (f) =>
      "(\n" +
      `      titulo: ${q(f.titulo)},\n` +
      `      centro: ${q(f.centro)},\n` +
      `      fechas: ${q(f.fechas)},\n` +
      "    )",
  );
  const skillItems = cv.habilidades.map(
    (h) => `(etiqueta: ${q(h.etiqueta)}, valor: ${q(h.valor)})`,
  );

  return (
    `${i0}#let cv = (\n` +
    `  perfil: ${perfilBlock},\n` +
    `  resumen: ${q(cv.resumen)},\n` +
    `  experiencia: ${arrayLines(expItems, "  ")},\n` +
    `  formacion: ${arrayLines(eduItems, "  ")},\n` +
    `  certificaciones: ${arrayLines(cv.certificaciones.map(q), "  ")},\n` +
    `  habilidades: ${arrayLines(skillItems, "  ")},\n` +
    `)`
  );
}

/** Replace the cv block in `source` with a freshly serialized one. */
export function applyCv(source: string, cv: CvData): string {
  const block = extractCvBlock(source);
  if (!block) return source;
  return source.slice(0, block.start) + serializeCv(cv) + source.slice(block.end);
}

/** Every string value in the CV, for the ATS text extractor. */
export function cvStrings(source: string): string[] {
  const block = extractCvBlock(source);
  if (!block) return [];
  const out: string[] = [];
  const walk = (v: Val) => {
    if (typeof v === "string") out.push(v);
    else if (Array.isArray(v)) v.forEach(walk);
    else Object.values(v).forEach(walk);
  };
  walk(block.value);
  return out;
}
